// schema
import * as ISchema from "@schema";
import * as React from "react";
import { connect } from "react-redux";

// meeting-card imports
import { styles } from "./AutopilotList.styles";

// other internals
import {
  EventSummaryTime,
  EventSummaryRecurrence,
  EventSummaryAutopilot,
} from "@components/event-summary";
import { H3 } from "@ui";

import { AutopilotListMode } from "./AutopilotList.d";

// components
import { CardTopLineOld } from "../card-top-line/CardTopLineOld";
import { asCard } from "@components/card";

// style
import { colors } from "@clockwise/web-commons/src/styles/color.styles";

// util
import { createEventParentRelayIdFromOrgRelayId, eventHasRoom } from "@util/event.util";
import { generateEventFormSaveTags, EFlex } from "@util/event-tag.util";
import { getScheduleText } from "@clockwise/client-commons/src/util/event";
import { isValidEmail } from "@clockwise/client-commons/src/util/email";

// state
import { IReduxState } from "@reducers/root.reducer";
import { updateOrgInvite, addAndRemoveOrgTags } from "@mutations";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { ChevronRight } from "@material-ui/icons";
import { toggleExpanded } from "@actions/settings.actions";
import { ZonedMoment } from "@util/time-zone.util";
import { setSettingsDialogOpen } from "@actions/chrome-extension.actions";
import { track, TrackingEvents } from "@util/analytics.util";

// material-ui
import { Button } from "@material-ui/core";
import { lighten } from "@material-ui/core/styles/colorManipulator";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";

///////////////////
// COMPONENT
////////////////////
export type TMeetingAction = "autopilot" | "invite";

interface IMeetingContainer {
  org: ISchema.IOrg;
  meeting: ISchema.IEvent;
  environment: any;
  personListErrorable: ISchema.PersonListErrorable;
  personMap: { [email: string]: ISchema.IPerson };
  onClickMeeting: (meeting: ISchema.IEvent) => void;
  removeCard: () => void;
  action?: TMeetingAction;
  isSelected?: boolean;
  mode: AutopilotListMode;
  calendarId: string;
}

interface IMeetingProps extends IMeetingContainer, WithStyles<typeof styles> {
  toggleExpanded: typeof toggleExpanded;
  setSettingsDialogOpen: typeof setSettingsDialogOpen;
  snackBarOpen: typeof snackBarOpen;
}

interface IMeetingState {}

export class MeetingCmpt extends React.Component<IMeetingProps, IMeetingState> {
  constructor(props: IMeetingProps) {
    super(props);
  }

  static contextType = TranslationContext;
  declare context: React.ContextType<typeof TranslationContext>;

  private onClickInvite = () => {
    const { org, meeting, environment } = this.props;
    const calendarId = meeting.organizer.urnValue;

    track(TrackingEvents.AUTOPILOT_LIST.INVITE_CLICKED, {
      calendarId,
      externalEventId: this.props.meeting.eventKey.externalEventId,
    });

    // this.props.setSettingsDialogOpen(true);
    // this.props.toggleExpanded('expandInvites');

    updateOrgInvite(
      environment,
      { calendarIds: [calendarId], orgRelayId: org.id },
      this.onInviteSuccess,
      this.onInviteFailure,
    );
  };

  private onInviteSuccess = () => {
    this.props.snackBarOpen("Invite sent!");
  };

  private onInviteFailure = () => {
    this.props.snackBarOpen("Sorry, there was a problem sending the invitation.");
  };

  private onClickAutopilot = () => {
    const { org, meeting, environment, calendarId } = this.props;
    track(TrackingEvents.AUTOPILOT_LIST.AUTOPILOT_CLICKED, {
      externalEventId: meeting.eventKey.externalEventId,
    });

    // actually enable autopilot
    const externalEventId = this.props.meeting.eventKey.externalEventId;
    const eventParentRelayId = createEventParentRelayIdFromOrgRelayId(org.id, externalEventId);
    if (!eventParentRelayId) {
      return;
    }

    // create tags for saving
    const hasRoom = eventHasRoom(this.props.meeting);

    const flex =
      (meeting.suggestedAutopilotSettingsResponseV3.__typename !== "GraphEntityError" &&
        ((meeting.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings
          .movementType as unknown) as EFlex)) ||
      EFlex.Day;

    const allowSimilarRooms =
      (meeting.suggestedAutopilotSettingsResponseV3.__typename !== "GraphEntityError" &&
        meeting.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.isRoomFlexible) ||
      hasRoom;

    const allowedStart =
      (meeting.suggestedAutopilotSettingsResponseV3.__typename !== "GraphEntityError" &&
        meeting.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.allowedStartTime) ||
      null;

    const allowedEnd =
      (meeting.suggestedAutopilotSettingsResponseV3.__typename !== "GraphEntityError" &&
        meeting.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.allowedEndTime) ||
      null;

    const tags = generateEventFormSaveTags(flex, undefined, undefined, allowSimilarRooms);

    // add the org id and eventparent id to the input
    const input: ISchema.IAddAndRemoveOrgTagsMutationInput = {
      eventParentRelayId,
      calendarId,
      moveRangeV3: {
        allowedStartTimeInclusive: allowedStart!,
        allowedEndTimeInclusive: allowedEnd!,
      },
      ...tags,
      orgRelayId: org.id,
    };

    // finally, mutate
    addAndRemoveOrgTags(environment, input, this.onSuccess, () => null);
  };

  private onSuccess = () => {
    this.props.snackBarOpen("Saved event preferences");
  };

  public render() {
    const { org, isSelected, meeting, action, personMap, mode, calendarId } = this.props;
    const startTime = meeting.startTime && new ZonedMoment(meeting.startTime.millis);
    const endTime = meeting.endTime && new ZonedMoment(meeting.endTime.millis);
    const t = this.context;

    const backgroundColor = {
      backgroundColor: isSelected ? lighten(colors.blueCool.lightest, 0.85) : undefined,
    };

    const topLine = getScheduleText(meeting, this.props.personListErrorable, t);

    const style = {
      ...styles.meeting,
      ...backgroundColor,
      ...(mode === AutopilotListMode.Menu
        ? {
            paddingLeft: 16,
            paddingTop: 16,
          }
        : {}),
    };

    const buttonStyle =
      mode === AutopilotListMode.Menu
        ? {
            marginBottom: 16,
          }
        : {};

    const organizerPerson = personMap[meeting.organizer.urnValue];
    const organizerName =
      organizerPerson && organizerPerson.profile
        ? organizerPerson.profile.givenName
        : meeting.organizer.urnValue;

    const isValidInviteEmail = isValidEmail(meeting.organizer.urnValue);

    return (
      <div cw-id="meeting-tile">
        <a
          className={this.props.classes.meeting}
          style={style}
          key={meeting.eventKey.externalEventId}
          onClick={() => this.props.onClickMeeting(meeting)}
        >
          <CardTopLineOld text={topLine} showToggle={false} open={false} />
          <H3 className={this.props.classes.h3}>{meeting.title}</H3>
          <EventSummaryAutopilot canAutopilot={action === "autopilot"} org={org} event={meeting} />
          {startTime ? (
            <EventSummaryTime
              org={org}
              event={meeting}
              startTime={startTime}
              endTime={endTime}
              unemphasized={true}
              calendarId={calendarId}
            />
          ) : null}
          {meeting.recurrenceRules ? (
            <EventSummaryRecurrence org={org} event={meeting} calendarId={calendarId} />
          ) : null}
          {mode === AutopilotListMode.Menu ? <ChevronRight style={styles.meetingChevron} /> : null}
        </a>
        {action === "autopilot" ? (
          <Button
            className={this.props.classes.cardButton}
            style={buttonStyle}
            onClick={this.onClickAutopilot}
          >
            <span className={this.props.classes.cardLabel}>
              {t("autopilot-list:autopilot-action-text")}
            </span>
          </Button>
        ) : null}
        {action === "invite" && isValidInviteEmail ? (
          <Button
            className={this.props.classes.cardButton}
            style={buttonStyle}
            onClick={this.onClickInvite}
          >
            <span className={this.props.classes.cardLabel}>Invite {organizerName}</span>
          </Button>
        ) : null}
      </div>
    );
  }
}

//////////////////
// STYLES
//////////////////
const MeetingStyled = withStyles(styles)(MeetingCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IMeetingContainer) {
  return {};
}

const MeetingRedux = connect(mapStateToProps, {
  setSettingsDialogOpen,
  toggleExpanded,
  snackBarOpen,
})(MeetingStyled);

export const MeetingCard = asCard<IMeetingContainer>(MeetingRedux);

export const Meeting = (props: IMeetingContainer) => {
  switch (props.mode) {
    case AutopilotListMode.Menu:
      return <MeetingRedux {...props} />;
    case AutopilotListMode.Card:
    default:
      return <MeetingCard {...props} />;
  }
};
